<template>
  <div class="payment">
    <vue-headful title="Payment"/>

    <ViewComponentLoader v-if="apiLoading" label="Loading your Payment" />
    <template v-if="apiLoaded">
      <div class="inspection-pg">
        <AddressTitleBar
          :hg-address="inspectionStreetAddress"
          :hgLocation="inspectionLocation"
          :hg-logo="inspection.company.companyLogo"
          class="title">
          <InspectionButton :hg-folder-id="folderId"/>
        </AddressTitleBar>
      </div>

      <PaymentForm
        :api-loading="apiLoading"
        :api-error="apiError"
        :api-success="apiSuccess"
        :company="company"
        :payment-defaults="paymentDefaults"
        :payment-gateway="paymentGateway"
        :additionalChargesSetting="companyAdditionalChargesSetting"
        @clearApiError="resetApiError"
        @submit="savePayment" />
    </template>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import BaseViewComponent from '@/components/Shared/BaseViewComponent.vue';
import ViewComponentLoader from '@/components/Shared/ViewComponentLoader.vue';
import AddressTitleBar from '../AddressBar/AddressTitleBar.vue';
import InspectionButton from '../AddressBar/InspectionButton.vue';
import PaymentForm from '../Payments/PaymentForm.vue';
import featureFlags from '../../constants/feature-flags';

export default {
  name: 'hgPayment',
  extends: BaseViewComponent,
  components: {
    AddressTitleBar,
    InspectionButton,
    PaymentForm,
    ViewComponentLoader,
  },
  props: {
    folderId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      company: {},
      apiError: null,
      apiLoading: false,
      apiSuccess: false,
    };
  },
  computed: {
    ...mapGetters('Inspection', [
      'inspectionStreetAddress',
      'inspectionLocation',
    ]),
    ...mapState('Inspection', {
      inspection: state => state.inspection,
    }),
    ...mapState('Payment', {
      paymentDefaults: state => state.paymentDefaults,
      paymentGateway: state => state.paymentGateway,
    }),
    ...mapState('Company', {
      companyAdditionalChargesSetting: state => state.company.additionalChargesSetting || {},
    }),
    ...mapState('unleash', {
      enabledFeatures: state => state.enabledFeatures,
    }),
    technologyFeeEnabled() {
      return this.enabledFeatures[featureFlags.TECHNOLOGY_FEE];
    },
    settingKey() {
      return this.technologyFeeEnabled ? 'TECHNOLOGY_FEE' : 'CREDIT_CARD_SURCHARGE';
    },
  },
  beforeRouteUpdate(to, from, next) {
    if (!to.hash) { /* ignore anchor tags for scrolling */
      this.created();
      next();
    }
  },
  methods: {
    resetApiError() {
      this.apiError = null;
    },
    async savePayment(paymentData) {
      this.apiLoading = true;
      this.apiError = null;
      this.apiSuccess = false;

      try {
        const response = await this.$store.dispatch('Payment/makePayment', paymentData);
        this.apiSuccess = true;
        await this.$router.replace({
          name: 'receipt',
          params: {
            companyId: this.inspection.company.id,
            folderId: this.folderId,
            paymentId: response.receiptId,
            paymentComplete: true,
          },
        });
      } catch (err) {
        this.apiError = err && err.body
          ? err.body
          : { message: 'Payment could not be processed', fieldViolations: [] };
      }

      this.apiLoading = false;
    },
  },
  async mounted() {
    await this.initialHydration([
      this.$store.dispatch('Payment/loadPayment', this.folderId),
      this.$store.dispatch('Payment/loadPaymentGateway', this.folderId),
      this.$store.dispatch('Inspection/loadInspection', this.folderId),
    ]);
    this.company = await this.$store.dispatch('Company/loadCompany', this.inspection.company.companyId) || {};
    await this.$store.dispatch('Company/loadCompanyAddtionalChargesSetting', { companyId: this.inspection.company.companyId, settingKey: this.settingKey });
    await this.$store.dispatch('Company/loadCompanyAddtionalChargesSettingEnabled', { companyId: this.inspection.company.companyId, settingKey: `${this.settingKey}_ENABLED` });
    this.company.phoneLink = this.inspection.company.companyPhone;
    if (!this.company.phone) {
      this.company.phone = this.inspection.company.companyPhone;
    }
    if (!this.company.email) {
      this.company.email = this.inspection.company.email;
    }
  },
};
</script>
